import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Page,
  Banner,
  Card,
  Text,
  Badge as PolarisBadge,
  Button as PolarisButton,
} from "@shopify/polaris";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import PrettyDate from "components/format/PrettyDate";
import { Column } from "types/Datatable";
import {
  fetchAssignmentAttempts,
  TrainingAttemptSummary,
  TrainingReviewItem,
} from "services/work-security-training.service";
import { optionLetter } from "./training-exam-ui";

function resultLabel(t: any, a: TrainingAttemptSummary) {
  if (a.passed) return t("test_passed", { defaultValue: "Passed" });
  if (a.status === "in_progress") {
    return t("training_status_in_progress", { defaultValue: "In progress" });
  }
  if (a.status === "timed_out") {
    return t("training_status_timed_out", { defaultValue: "Timed out" });
  }
  return t("test_failed", { defaultValue: "Failed" });
}

function resultTone(a: TrainingAttemptSummary): any {
  if (a.passed) return "success";
  if (a.status === "in_progress") return "attention";
  return "critical";
}

function selectedIds(item: TrainingReviewItem): string[] {
  if (item.selectedOptionIds?.length) return item.selectedOptionIds.map(String);
  if (item.selectedOptionId) return [String(item.selectedOptionId)];
  return [];
}

export default function TrainingHistoryContainer() {
  const { companyId, assignmentId } = useParams<{
    companyId: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState<TrainingAttemptSummary[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId || !assignmentId) return;
    setLoading(true);
    try {
      const data = await fetchAssignmentAttempts(companyId, assignmentId);
      setAttempts(data.attempts || []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("load_failed", { defaultValue: "Failed to load history" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, assignmentId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column[] = useMemo(
    () => [
      {
        key: "attemptLabel",
        name: t("attempts", { defaultValue: "Attempts" }),
        mobileView: "topLeft",
      },
      {
        key: "result",
        name: t("status", { defaultValue: "Status" }),
        type: "custom",
        mobileView: "topRight",
        render: (item: any) => (
          <PolarisBadge tone={resultTone(item)}>
            {resultLabel(t, item)}
          </PolarisBadge>
        ),
      },
      {
        key: "scoreLabel",
        name: t("last_score", { defaultValue: "Last score" }),
        mobileView: "bottomLeft",
      },
      {
        key: "startedAtIso",
        name: t("started_at", { defaultValue: "Started" }),
        type: "datetime",
      },
      {
        key: "submittedAtIso",
        name: t("submitted_at", { defaultValue: "Submitted" }),
        type: "datetime",
      },
      {
        key: "actions",
        name: "",
        type: "custom",
        mobileView: "bottomRight",
        render: (item: any) =>
          item.review?.length ? (
            <PolarisButton
              size="slim"
              onClick={() =>
                setExpanded((cur) => (cur === item.id ? null : item.id))
              }
            >
              {expanded === item.id
                ? t("hide_answers", { defaultValue: "Hide answers" })
                : t("view_submitted_answers", {
                    defaultValue: "View submitted answers",
                  })}
            </PolarisButton>
          ) : (
            "—"
          ),
      },
    ],
    [t, expanded]
  );

  const collection = attempts.map((a) => ({
    ...a,
    attemptLabel: t("attempt_n", {
      defaultValue: "Attempt {{n}}",
      n: a.attemptNumber,
    }),
    scoreLabel: a.scorePercent != null ? `${a.scorePercent}%` : "—",
    startedAtIso: a.startedAt ? new Date(a.startedAt).toISOString() : "",
    submittedAtIso: a.submittedAt
      ? new Date(a.submittedAt).toISOString()
      : "",
  }));

  const expandedAttempt = attempts.find((a) => a.id === expanded);

  return (
    <Page
      fullWidth
      title={t("attempt_history", { defaultValue: "Attempt history" })}
      backAction={{
        onAction: () =>
          navigate(
            `/app/${companyId}/my-work/training/${assignmentId}?step=test`
          ),
      }}
    >
      <div className="space-y-4 pb-10">
        {error && (
          <Banner tone="critical" onDismiss={() => setError("")}>
            {error}
          </Banner>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
          {loading ? (
            <DataTableSkeleton rows={6} columns={columns.length} />
          ) : attempts.length === 0 ? (
            <div className="text-center py-16 px-4 text-sm text-zinc-500">
              {t("no_attempts", { defaultValue: "No attempts yet." })}
            </div>
          ) : (
            <DataTable collection={collection} columns={columns} />
          )}
        </div>

        {expandedAttempt?.review && expandedAttempt.review.length > 0 && (
          <Card>
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text as="h3" variant="headingMd">
                  {t("submitted_answers", {
                    defaultValue: "Submitted answers",
                  })}
                  {" · "}
                  {t("attempt_n", {
                    defaultValue: "Attempt {{n}}",
                    n: expandedAttempt.attemptNumber,
                  })}
                </Text>
                {expandedAttempt.submittedAt ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    <PrettyDate
                      dateString={new Date(
                        expandedAttempt.submittedAt
                      ).toISOString()}
                      showTime
                    />
                  </Text>
                ) : null}
              </div>

              <Text as="p" variant="bodySm" tone="subdued">
                {t("submitted_answers_hint", {
                  defaultValue:
                    "Questions and options from this attempt. Your selected answer(s) are highlighted.",
                })}
              </Text>

              {(expandedAttempt.review as TrainingReviewItem[]).map(
                (item, i) => {
                  const chosen = selectedIds(item);
                  return (
                    <div
                      key={item.questionId}
                      className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700"
                    >
                      <Text as="p" variant="headingSm">
                        {i + 1}. {item.questionText}
                      </Text>
                      <ul className="mt-3 space-y-2">
                        {(item.options || []).map((opt, oi) => {
                          const isSelected = chosen.includes(String(opt.id));
                          return (
                            <li
                              key={opt.id}
                              className={`rounded-md border px-3 py-2 text-sm ${
                                isSelected
                                  ? "border-sky-400 bg-sky-50 font-medium text-sky-950 dark:border-sky-600 dark:bg-sky-950/40 dark:text-sky-100"
                                  : "border-zinc-100 text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                              }`}
                            >
                              <span className="mr-2 text-zinc-500">
                                {optionLetter(oi)}.
                              </span>
                              {opt.text}
                              {isSelected ? (
                                <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                                  ({t("your_answer", {
                                    defaultValue: "your answer",
                                  })})
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                      {chosen.length === 0 ? (
                        <p className="mt-2 text-sm text-zinc-500 italic">
                          {t("no_answer", { defaultValue: "No answer" })}
                        </p>
                      ) : null}
                    </div>
                  );
                }
              )}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
